/**
 * PROJECT IDENTIFICATION HELPERS
 *
 * Pure functions to map terminal scopes and working directories to project IDs.
 * Used by the unified "My Agents" view to group terminals by project.
 */

import type { Project } from "@/stores/worldStore";

/**
 * Match a working directory to a project by comparing against `localPath`.
 * Supports nested subdirectory matching: cwd `/foo/bar/src` matches
 * a project with `localPath: "/foo/bar"`.
 *
 * Returns the project ID of the best (longest path) match, or `null`.
 */
export function getProjectIdForCwd(
  cwd: string,
  projects: Project[]
): number | null {
  if (!cwd) return null;

  const normalized = normalizePath(cwd);
  let bestMatch: Project | null = null;
  let bestLength = 0;

  for (const project of projects) {
    if (!project.localPath) continue;
    const projectPath = normalizePath(project.localPath);
    if (!projectPath) continue;

    // cwd must equal or be a subdirectory of the project path
    if (
      normalized === projectPath ||
      normalized.startsWith(projectPath + "/")
    ) {
      // Prefer the longest (most specific) match
      if (projectPath.length > bestLength) {
        bestLength = projectPath.length;
        bestMatch = project;
      }
    }
  }

  return bestMatch?.id ?? null;
}

/**
 * Extract a project ID from a localTerminalStore scope key.
 *
 * Scope key formats observed:
 * - `"local:<wsId>:project:<projectId>"` — from ProjectDetailView
 * - `"cwd:/some/path"` — default from LocalTerminalView
 *
 * Strategy:
 * 1. Try to extract a numeric project ID from the scope key pattern
 * 2. If the key is path-based (`cwd:...`), fall back to cwd matching
 */
export function extractProjectIdFromScope(
  scopeKey: string,
  projects: Project[]
): number | null {
  if (!scopeKey) return null;

  // Pattern 1: "local:<wsId>:project:<id>" or any string ending with ":project:<digits>"
  const projectIdMatch = scopeKey.match(/:project:(\d+)$/);
  if (projectIdMatch) {
    const id = Number(projectIdMatch[1]);
    // Validate the ID actually exists in the projects list
    if (projects.some((p) => p.id === id)) {
      return id;
    }
  }

  // Pattern 2: "cwd:/some/path" — extract path and match against projects
  if (scopeKey.startsWith("cwd:")) {
    const cwd = scopeKey.slice(4);
    return getProjectIdForCwd(cwd, projects);
  }

  return null;
}

/** Strip trailing slashes for consistent comparison. */
function normalizePath(p: string): string {
  return p.replace(/\/+$/, "");
}
